import { ChevronDown, ChevronRight, MoreHorizontal, Pin } from "lucide-react";
import { useState } from "react";

import { useApproveForJD, useRemoveJDLink, type JDQuestion } from "@/api/jd-question-queries";
import { SourceChip } from "@/components/jd-source-chip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Menu } from "@/components/ui/menu";
import { ErrorNote } from "@/components/ui/panel";
import { useDraft } from "@/lib/jd-draft-state";
import { placeOf } from "@/lib/jd-steps";
import { cn, formatDate } from "@/lib/utils";

/**
 * The rows of the Questions step — DESIGN §20.6, §20.7. A JD's own question says where it stands in
 * words beside its status chip, because "pending_review" tells a Resource Manager less than "waiting
 * for a reviewer"; a matched one sits under the skill it matched, which answers "why is this here?".
 */

export const OWN_GRID =
  "grid grid-cols-[28px_minmax(280px,1fr)_210px_130px_110px_150px] gap-3 min-w-[1040px]";

const SCOPE_LABEL = { jd_only: "This JD only", bank: "Whole bank" } as const;

const PLACE_TONE = {
  good: "text-bank-good-ink",
  attention: "text-bank-attention-ink",
  pipe: "text-bank-pipe-ink",
  muted: "text-bank-muted",
} as const;

/** What the question tests: the concept it was tagged with at enrichment, then the smaller ideas under it. */
function Concepts({ q }: { q: JDQuestion }) {
  if (!q.primary_concept && q.sub_concepts.length === 0) return null;
  return (
    <p className="mt-1.5 flex flex-wrap items-center gap-1 text-[11.5px]">
      {q.primary_concept && (
        <span
          className="bg-bank-accent-wash text-bank-accent-ink rounded px-1.5 py-px font-medium"
          title="Concept"
        >
          {q.primary_concept}
        </span>
      )}
      {q.sub_concepts.map((c) => (
        <span
          key={c}
          className="border-bank-border text-bank-muted rounded border px-1.5 py-px"
          title="Sub-concept"
        >
          {c}
        </span>
      ))}
    </p>
  );
}

function Meta({ q }: { q: JDQuestion }) {
  return (
    <p className="text-bank-muted mt-0.5 font-mono text-[11px]">
      #{q.question_id} · {q.stack_path ?? "not enriched yet"}
      {q.difficulty_score ? ` · difficulty ${q.difficulty_score}` : ""}
    </p>
  );
}

export function OwnRow({
  q: saved,
  jdId,
  canManage,
  selfApprove,
  checked,
  onCheck,
}: {
  q: JDQuestion;
  jdId: number;
  canManage: boolean;
  selfApprove: boolean;
  checked: boolean | null;
  onCheck: () => void;
}) {
  const draft = useDraft();
  const q = draft.link(saved);
  const remove = useRemoveJDLink(jdId);
  const approve = useApproveForJD(jdId);
  const place = placeOf(q);
  const waiting = q.status === "pending_review" || q.status === "stale";
  const changed = q.is_excluded !== saved.is_excluded;

  return (
    <div
      className={cn(
        OWN_GRID,
        "border-bank-border items-start border-t px-4 py-3",
        q.is_excluded && "text-bank-muted",
      )}
    >
      <span>
        {checked !== null && (
          <input
            type="checkbox"
            checked={checked}
            onChange={onCheck}
            aria-label={`Select question #${q.question_id}`}
            className="accent-bank-accent mt-1 size-3.5"
          />
        )}
      </span>
      <div className="min-w-0">
        <p className={cn("text-[13.5px]", q.is_excluded && "line-through")}>{q.question_text}</p>
        <Meta q={q} />
        <Concepts q={q} />
        {changed && <p className="text-bank-attention-ink mt-0.5 text-[11px]">changed, not saved</p>}
        <ErrorNote error={remove.error ?? approve.error} />
      </div>
      <div>
        <Badge value={place.status} />
        <p className={cn("mt-1 text-[12.5px]", PLACE_TONE[place.tone])}>{place.words}</p>
      </div>
      <div className="text-[12.5px]">
        {q.canonical_id !== null ? (
          <span className="text-bank-muted">Whole bank (as #{q.canonical_id})</span>
        ) : (
          <span className="bg-bank-accent-wash text-bank-accent-ink rounded px-1.5 py-0.5">
            {SCOPE_LABEL[q.scope]}
          </span>
        )}
        {waiting && q.canonical_id === null && (
          <span className="text-bank-muted mt-1 block text-[11.5px]">suggested</span>
        )}
      </div>
      <div>
        <SourceChip source={`jd_${q.origin}`} />
      </div>
      <div className="flex flex-col items-end gap-1">
        <span className="text-bank-muted font-mono text-[11px]">{formatDate(q.added_at)}</span>
        {q.after_publish && <span className="text-bank-muted text-[11px]">added after publishing</span>}
        {canManage && (
          <div className="flex items-center gap-1">
            {selfApprove && waiting && (
              <Button
                size="sm"
                disabled={approve.isPending}
                title="Approve to this JD only — a Maintainer decides whether it joins the bank"
                onClick={() => approve.mutate(q.question_id)}
              >
                Approve for this JD
              </Button>
            )}
            {q.is_excluded ? (
              <Button size="sm" onClick={() => draft.patchLink(q.question_id, { is_excluded: false })}>
                Restore
              </Button>
            ) : (
              <Menu
                label={`More for question #${q.question_id}`}
                size="sm"
                variant="ghost"
                iconOnly
                trigger={<MoreHorizontal className="size-4" aria-hidden="true" />}
                items={[
                  {
                    label: "Exclude from interviews",
                    hint: "Stays linked; never drawn for this JD.",
                    onSelect: () => draft.patchLink(q.question_id, { is_excluded: true }),
                  },
                  {
                    label: "Remove from this JD",
                    hint: "Straight away. The question stays in the bank.",
                    disabled: remove.isPending,
                    onSelect: () => remove.mutate(q.question_id),
                  },
                ]}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MatchedRow({ q: saved, canManage }: { q: JDQuestion; canManage: boolean }) {
  const draft = useDraft();
  const q = draft.link(saved);
  const score = q.score ?? 0;
  return (
    <div
      className={cn(
        "border-bank-border grid grid-cols-[minmax(240px,1fr)_110px_200px] items-start gap-3 border-t px-4 py-2.5",
        q.is_pinned && "bg-bank-accent-wash",
        q.is_excluded && "text-bank-muted",
      )}
    >
      <div className="min-w-0">
        <p className={cn("text-[13.5px]", q.is_excluded && "line-through")}>{q.question_text}</p>
        <Meta q={q} />
        {!q.is_excluded && <Concepts q={q} />}
        {q.is_excluded && <p className="text-[11.5px]">Excluded — never asked for this JD</p>}
      </div>
      <div>
        <span className="font-mono text-xs tabular-nums">{q.score === null ? "—" : q.score.toFixed(2)}</span>
        <div className="bg-bank-surface-3 mt-1 h-[5px] w-[70px] overflow-hidden rounded-full">
          <div
            className={cn("h-full rounded-full", q.is_excluded ? "bg-bank-border-strong" : "bg-bank-accent")}
            style={{ width: `${Math.round(score * 100)}%` }}
          />
        </div>
      </div>
      <div className="flex flex-col items-end gap-1">
        {canManage &&
          (q.is_excluded ? (
            <Button size="sm" onClick={() => draft.patchLink(q.question_id, { is_excluded: false })}>
              Restore
            </Button>
          ) : (
            <div className="flex gap-1">
              <Button
                size="sm"
                variant={q.is_pinned ? "primary" : "secondary"}
                aria-pressed={q.is_pinned}
                onClick={() => draft.patchLink(q.question_id, { is_pinned: !q.is_pinned })}
              >
                <Pin className="size-3.5" aria-hidden="true" />
                {q.is_pinned ? "Pinned" : "Pin"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => draft.patchLink(q.question_id, { is_excluded: true })}
              >
                Exclude
              </Button>
            </div>
          ))}
        {q.is_pinned && !q.is_excluded && (
          <span className="text-bank-accent-ink text-[11.5px]">Always asked first for this JD</span>
        )}
      </div>
    </div>
  );
}

// Enough to judge a skill's matches by; the rest wait behind a click.
const SHOWN = 5;

export function MatchedGroup({
  title,
  meta,
  items,
  canManage,
  defaultOpen,
}: {
  title: string;
  meta: string;
  items: JDQuestion[];
  canManage: boolean;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [all, setAll] = useState(false);
  const pinned = items.filter((q) => q.is_pinned).length;
  const shown = all ? items : items.slice(0, SHOWN);
  return (
    <section>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="border-bank-border bg-bank-surface-2 hover:bg-bank-surface-3 flex w-full flex-wrap items-center gap-x-2.5 gap-y-1 border-t px-4 py-2 text-left"
      >
        {open ? (
          <ChevronDown className="size-4" aria-hidden="true" />
        ) : (
          <ChevronRight className="size-4" aria-hidden="true" />
        )}
        <span className="text-[13.5px] font-semibold">{title}</span>
        <span className="text-bank-muted font-mono text-[11.5px]">{meta}</span>
        <span className="text-bank-muted text-[12.5px]">
          {items.length} matched{pinned ? ` · ${pinned} pinned` : ""}
        </span>
      </button>
      {open && (
        <>
          {shown.map((q) => (
            <MatchedRow key={q.question_id} q={q} canManage={canManage} />
          ))}
          {items.length > SHOWN && (
            <div className="border-bank-border border-t px-4 py-1.5">
              <Button size="sm" variant="ghost" onClick={() => setAll(!all)}>
                {all ? "Show fewer" : `Show ${items.length - SHOWN} more for ${title}`}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
